// Unpublished Work © 2022-2026 Deere & Company.

import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import {fetchEffectData} from 'Utils/react-utils';
import {getEquipmentCosts, getEquipmentCostsRange} from 'Services/equipment-service';
import CostReportChart from 'Ui/features/onequip/reports/cost-report/cost-report-chart';
import CostReportDataTable from 'Ui/features/onequip/reports/cost-report/cost-report-data-table';
import NoDataMessage from 'Ui/components/common/message/no-data-message';
import {useNavBarActions} from 'Ui/react-hooks/use-navbar-actions';
import {IconWdt} from '@deere/icons';
import {VIEW_EQUIPMENT_DETAILS} from 'Common/constants/business-activities';
import {exportEquipmentCostReport} from 'Services/excel-service';
import LaborReportExportDialog from 'OnLabor/report/labor-report-export-dialog';
import {getEquipmentCostExportContent} from 'Utils/labor-report-export-utils';
import useReportExportDialog from 'Ui/react-hooks/use-report-export-dialog';
import moment from 'moment';

function aggregateData(equipmentCostRollup) {
    const aggregatedData = {
        advanceLeasePayments: 0,
        leasePayments: 0,
        partsCost: 0,
        propertyTax: 0,
        purchase: 0,
        salesTax: 0,
        serviceCost: 0,
        totalCost: 0,
        finalLeasePayments: 0
    };

    equipmentCostRollup.dateChildren?.forEach((rollupData) => {
        aggregatedData.advanceLeasePayments += rollupData.advanceLeasePayments;
        aggregatedData.leasePayments += rollupData.leasePayments;
        aggregatedData.finalLeasePayments += rollupData.finalLeasePayments;
        aggregatedData.partsCost += rollupData.partsCost;
        aggregatedData.propertyTax += rollupData.propertyTax;
        aggregatedData.purchase += rollupData.purchase;
        aggregatedData.salesTax += rollupData.salesTax;
        aggregatedData.serviceCost += rollupData.serviceCost;
        aggregatedData.totalCost += rollupData.totalCost - (rollupData.finalLeasePayments + rollupData.leasePayments + rollupData.advanceLeasePayments);
    });

    return aggregatedData;
}

function getDateChildren(equipmentCostRollup) {
    return equipmentCostRollup.dateChildren?.map((rollup) => {
        return {
            ...rollup,
            totalCost: rollup.totalCost - (rollup.finalLeasePayments + rollup.leasePayments + rollup.advanceLeasePayments)
        };
    });
}

function transformData(equipmentCostRollup) {
    if (equipmentCostRollup.dateScale === 'daily') {
        return {
            ...equipmentCostRollup,
            totalCost: equipmentCostRollup.totalCost -
                (equipmentCostRollup.finalLeasePayments + equipmentCostRollup.leasePayments + equipmentCostRollup.advanceLeasePayments),
            dateChildren: [{
                advanceLeasePayments: equipmentCostRollup.advanceLeasePayments,
                dateScale: equipmentCostRollup.dateScale,
                dateSelect: equipmentCostRollup.dateSelect,
                leasePayments: equipmentCostRollup.leasePayments,
                partsCost: equipmentCostRollup.partsCost,
                propertyTax: equipmentCostRollup.propertyTax,
                purchase: equipmentCostRollup.purchase,
                salesTax: equipmentCostRollup.salesTax,
                serviceCost: equipmentCostRollup.serviceCost,
                totalCost: equipmentCostRollup.totalCost -
                    (equipmentCostRollup.finalLeasePayments + equipmentCostRollup.leasePayments + equipmentCostRollup.advanceLeasePayments)
            }]
        };
    }

    return {
        ...equipmentCostRollup,
        ...aggregateData(equipmentCostRollup),
        dateChildren: getDateChildren(equipmentCostRollup)
    };
}

async function fetchEquipmentCosts(tertiarySelector, dateSelect, startDate, endDate) {
    try {
        const {equipmentCostRollup = {}} = startDate && endDate ?
            await getEquipmentCostsRange(tertiarySelector, startDate, endDate) :
            await getEquipmentCosts(tertiarySelector, dateSelect);

        return equipmentCostRollup;
    } catch {
        return {};
    }
}

function checkForEmptyData(equipmentCostRollup) {
    return !equipmentCostRollup || Object.keys(equipmentCostRollup).length === 0 || !equipmentCostRollup.dateChildren || equipmentCostRollup.dateChildren.length === 0;
}

function EquipmentCostReport(props) {
    const {
        dateSelect,
        endDate,
        isExportPreviewEnabled,
        membership,
        addToast,
        setLoading,
        startDate,
        tertiarySelector,
        translations
    } = props;

    const [equipmentCostRollup, setEquipmentCostRollup] = React.useState({});
    const exportContent = React.useMemo(() => getEquipmentCostExportContent(equipmentCostRollup, translations), [equipmentCostRollup, translations]);
    const dateRange = startDate && endDate ?
        `${moment(startDate, 'YYYYMMDD').format('MMM D, YYYY')} - ${moment(endDate, 'YYYYMMDD').format('MMM D, YYYY')}` :
        moment(dateSelect).format('MMM D, YYYY');
    const downloadReport = React.useCallback(() => exportEquipmentCostReport(equipmentCostRollup, translations, addToast), [equipmentCostRollup, translations, addToast]);
    const {
        handleClose,
        handleDownload,
        handleOpen,
        isOpen
    } = useReportExportDialog(downloadReport, isExportPreviewEnabled);

    React.useEffect(() => fetchEffectData(async (isMounted) => {
        let equipmentCostRollupData = {};

        if (tertiarySelector && (dateSelect || startDate && endDate)) {
            equipmentCostRollupData = await fetchEquipmentCosts(tertiarySelector, dateSelect, startDate, endDate);
        }

        if (isMounted()) {
            const transformedData = Object.keys(equipmentCostRollupData).length > 0 ?
                transformData(equipmentCostRollupData) :
                {};

            setEquipmentCostRollup(transformedData);
            setLoading((prevLoading) => ({
                ...prevLoading,
                dateSelect: false,
                primarySelector: false,
                secondarySelector: false,
                tertiarySelector: false
            }));
        }
    }), [dateSelect, endDate, startDate, tertiarySelector]);

    useNavBarActions([{
        disabled: false,
        Icon: <IconWdt/>,
        onClick: handleOpen,
        myJdPermissions: VIEW_EQUIPMENT_DETAILS,
        title: 'EXPORT',
        variant: 'primary'
    }]);

    return (
        <>
            <NoDataMessage
                hasData={!checkForEmptyData(equipmentCostRollup)}
                noDataMessage={translations.ONLINK_NO_DATA_GRAPH}
            >
                <div>
                    <div className='chart-container'>
                        <CostReportChart
                            costReportData={equipmentCostRollup}
                            membership={membership}
                            translations={translations}
                        />
                    </div>
                    <CostReportDataTable
                        costReportData={equipmentCostRollup}
                        membership={membership}
                        translations={translations}
                    />
                </div>
            </NoDataMessage>
            {
                isOpen &&
                <LaborReportExportDialog
                    dateRange={dateRange}
                    onCancel={handleClose}
                    onDownload={handleDownload}
                    rows={[
                        exportContent.header,
                        ...exportContent.rows,
                        ...exportContent.footer ? [exportContent.footer] : []
                    ]}
                    translations={translations}
                />
            }
        </>
    );
}

EquipmentCostReport.propTypes = {
    addToast: PropTypes.func,
    dateSelect: PropTypes.string,
    endDate: PropTypes.string,
    isExportPreviewEnabled: PropTypes.bool,
    membership: PropTypes.membership,
    setLoading: PropTypes.func,
    startDate: PropTypes.string,
    tertiarySelector: PropTypes.string,
    translations: PropTypes.translations
};

export default EquipmentCostReport;
